// The numbers strip (Console D): an ink top rule, mono figures, never KPI cards. Six cells a row
// on a wide screen, three under 1280 px and two under 960 px; the last row never ends in an
// empty cell (styles/components.css). Under 640 px every strip is two columns with a smaller
// figure, and a figure too long for half the row takes the whole row (the layout is worked out
// here, see narrowLayout). A fit strip (the admin's 4-cell strips, set app-wide with StripFit):
// four across while the strip itself is about 900 px wide, else two by two, by a container query
// on the strip, never 3 + 1. The console keeps the mock's rules. The line under a figure wraps
// only between its ' · ' parts, each part kept whole (subChunks).
import {
  Children,
  type CSSProperties,
  createContext,
  Fragment,
  isValidElement,
  type ReactNode,
  useContext,
} from 'react'

/** An app's 4-cell strips as fit strips (the admin's entry provides true). */
export const StripFit = createContext(false)

export interface StripItem {
  /** The kicker, in mono caps. */
  dt: ReactNode
  /** A shorter kicker under 640 px, when `dt` would not fit half the row. */
  dtShort?: ReactNode
  /** The figure. */
  dd: ReactNode
  /** Its unit, smaller beside it. */
  small?: ReactNode
  /** One line under it: the delta, the base, what it counts (wraps rather than clips). */
  d?: ReactNode
  /** The view's one accent: the cell this page is about. */
  cur?: boolean
  /** The cell as a link: the caller's link element with the given class and children (the
   * kicker), stretched over the cell. */
  link?: (props: { className: string; children: ReactNode }) => ReactNode
}

/** The characters a node renders as text (elements by their children). */
function textLength(node: ReactNode): number {
  if (typeof node === 'string' || typeof node === 'number') return String(node).length
  if (isValidElement<{ children?: ReactNode }>(node)) return textLength(node.props.children)
  let n = 0
  Children.forEach(node, (child) => {
    if (child !== node) n += textLength(child)
  })
  return n
}

/** A node's pieces in order: fragments and arrays opened, adjacent text joined. */
function pieces(node: ReactNode, out: ReactNode[] = []): ReactNode[] {
  Children.forEach(node, (child) => {
    if (child === null || child === undefined || typeof child === 'boolean') return
    if (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
      pieces(child.props.children, out)
    } else if (typeof child === 'string' || typeof child === 'number') {
      const last = out[out.length - 1]
      if (typeof last === 'string') out[out.length - 1] = last + String(child)
      else out.push(String(child))
    } else out.push(child)
  })
  return out
}

/**
 * The line under a figure in its ' · ' parts (APS-412, Mosaic): each part wraps as a whole, so a
 * wrapped line never breaks inside a phrase ('2,208 frozen frames · 5.3.0 1.2').
 */
export function subChunks(d: ReactNode): ReactNode[][] {
  const chunks: ReactNode[][] = [[]]
  for (const p of pieces(d)) {
    const current = chunks[chunks.length - 1] as ReactNode[]
    if (typeof p !== 'string') {
      current.push(p)
      continue
    }
    p.split(/\s+·\s+/).forEach((part, i) => {
      if (i > 0) chunks.push([])
      if (part) (chunks[chunks.length - 1] as ReactNode[]).push(part)
    })
  }
  return chunks.filter((c) => c.length > 0)
}

/** Half a 390 px row holds about 11 mono characters at 22 px; the unit is set at 13 px. */
const LONG = 10.5
/** ... and about 20 of the line under it (11.5 px); a longer part takes the whole row. */
const LONG_SUB = 20
const isLong = (x: StripItem) =>
  textLength(x.dd) + (x.small ? 0.6 * textLength(x.small) + 0.3 : 0) > LONG ||
  (x.d ? Math.max(0, ...subChunks(x.d).map((c) => textLength(c))) > LONG_SUB : false)

function Sub({ d }: { d: ReactNode }) {
  const chunks = subChunks(d)
  return (
    <dd className="d">
      {chunks.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the parts keep their order
        <Fragment key={i}>
          {i > 0 ? ' ' : null}
          <span className="d-c">
            {c.map((p, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the pieces keep their order
              <Fragment key={j}>{p}</Fragment>
            ))}
            {i < chunks.length - 1 ? ' ·' : null}
          </span>
        </Fragment>
      ))}
    </dd>
  )
}

/**
 * The two-column layout under 640 px: a long figure takes the whole row, the cell before it then
 * takes its row too, and so does a last cell left alone, so no row ends in an empty cell. Each
 * cell gets its classes: n-w2 (both columns), n-l (rule on its left), n-t (rule above it).
 */
export function narrowLayout(items: StripItem[]): string[] {
  const cells: { w2: boolean; row: number; col: number }[] = []
  let row = 0
  let col = 0
  items.forEach((x, i) => {
    const long = isLong(x)
    const before = cells[i - 1]
    if (long && col === 1 && before) {
      before.w2 = true
      row++
      col = 0
    }
    cells.push({ w2: long, row, col })
    if (long || col === 1) {
      row++
      col = 0
    } else col = 1
  })
  const last = cells[cells.length - 1]
  if (col === 1 && last) last.w2 = true
  return cells.map((c) =>
    [c.w2 ? 'n-w2' : '', !c.w2 && c.col === 1 ? 'n-l' : '', c.row > 0 ? 'n-t' : '']
      .filter(Boolean)
      .join(' '),
  )
}

export function Strip({
  items,
  label,
  translate,
  fit,
}: {
  items: StripItem[]
  label?: string
  translate?: 'no'
  /** A 4-cell strip: four across when it is wide enough, else two by two (default: StripFit). */
  fit?: boolean
}) {
  const appFit = useContext(StripFit)
  const narrow = narrowLayout(items)
  const fits = (fit ?? appFit) && items.length === 4
  const strip = (
    <dl
      className="strip"
      data-n={items.length}
      data-fit={fits ? '' : undefined}
      style={{ '--n': items.length } as CSSProperties}
      aria-label={label}
      translate={translate}
    >
      {items.map((x, i) => {
        const kicker = x.dtShort ? (
          <>
            <span className="dt-l">{x.dt}</span>
            <span className="dt-s">{x.dtShort}</span>
          </>
        ) : (
          x.dt
        )
        const inner = (
          <>
            <dt>{x.link ? x.link({ className: 'strip-a', children: kicker }) : kicker}</dt>
            <dd>
              {x.dd}
              {x.small ? <small>{x.small}</small> : null}
            </dd>
            {x.d ? <Sub d={x.d} /> : null}
          </>
        )
        const cls = [x.cur ? 'cur' : '', x.link ? 'has-a' : '', narrow[i] ?? '']
          .filter(Boolean)
          .join(' ')
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: a strip's cells keep their order
          <div key={i} className={cls || undefined}>
            {inner}
          </div>
        )
      })}
    </dl>
  )
  return fits ? <div className="strip-fit">{strip}</div> : strip
}
